import * as React from "react";
import type { SeriesPoint } from "../api.js";

const palette = ["#2ea66a", "#f5a623", "#d94c4c", "#14a89a", "#1a8fbf", "#7c5cff", "#e94e77", "#ff7a59"];

export function ChartCard({ title, sub, wide, children }: { title: string; sub?: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={"chart-card" + (wide ? " wide" : "")}>
      <div className="chart-card-h">
        <div>
          <div className="chart-card-t">{title}</div>
          {sub && <div className="chart-card-s">{sub}</div>}
        </div>
      </div>
      <div className="chart-card-body">{children}</div>
      <style>{cardCss}</style>
    </div>
  );
}

export function BarChart({ title, data, sub }: { title: string; data: SeriesPoint[]; sub?: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <ChartCard title={title} sub={sub}>
      {data.length === 0 ? <div className="empty-state" style={{ margin: "auto" }}>No data</div> : (
        <div className="hist">
          {data.map((d, i) => (
            <div key={i} className="hist-col">
              <div className="hist-bar" style={{ height: `${(d.value / max) * 100}%` }} title={`${d.label}: ${d.value}`}>
                <span className="hist-n">{d.value}</span>
              </div>
              <div className="hist-l">{d.label}</div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function Donut({ title, data, sub }: { title: string; data: SeriesPoint[]; sub?: string }) {
  const total = data.reduce((s, p) => s + p.value, 0);
  const SIZE = 180, R = 70, SW = 26;
  const c = SIZE / 2;
  const circ = 2 * Math.PI * R;
  let acc = 0;

  return (
    <ChartCard title={title} sub={sub}>
      {total === 0 ? <div className="empty-state" style={{ margin: "auto" }}>No data</div> : (
        <div style={{ display: "flex", alignItems: "center", gap: 24, flex: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            <circle cx={c} cy={c} r={R} fill="none" stroke="#f1f5f9" strokeWidth={SW} />
            {data.map((d, i) => {
              const frac = d.value / total;
              const dash = frac * circ;
              const offset = -acc * circ;
              acc += frac;
              return (
                <circle
                  key={i}
                  cx={c}
                  cy={c}
                  r={R}
                  fill="none"
                  stroke={palette[i % palette.length]}
                  strokeWidth={SW}
                  strokeDasharray={`${dash} ${circ}`}
                  strokeDashoffset={offset}
                  transform={`rotate(-90 ${c} ${c})`}
                  strokeLinecap="butt"
                />
              );
            })}
            <text x={c} y={c - 2} textAnchor="middle" fontSize="28" fontWeight="800" fill="#0d1b2a">{total}</text>
            <text x={c} y={c + 16} textAnchor="middle" fontSize="11" fill="#94a3b8" letterSpacing="0.06em">TOTAL</text>
          </svg>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 140 }}>
            {data.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: palette[i % palette.length], flexShrink: 0 }} />
                <span style={{ flex: 1, color: "var(--ink-700)" }}>{d.label}</span>
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{d.value}</span>
                <span style={{ color: "var(--ink-400)", fontSize: 12 }}>{Math.round((d.value / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartCard>
  );
}

export function LineAreaChart({ title, sub, perPeriod, cumulative, periodLabel = "Per week" }: {
  title: string;
  sub?: string;
  perPeriod: SeriesPoint[];
  cumulative: SeriesPoint[];
  periodLabel?: string;
}) {
  const W = 800, H = 220, PAD = { l: 36, r: 16, t: 14, b: 26 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  if (perPeriod.length < 2) {
    return (
      <ChartCard title={title} sub={sub} wide>
        <div className="empty-state" style={{ margin: "auto" }}>Not enough data yet.</div>
      </ChartCard>
    );
  }

  const maxBar = Math.max(1, ...perPeriod.map((p) => p.value));
  const maxCum = Math.max(1, ...cumulative.map((p) => p.value));
  const n = perPeriod.length;
  const x = (i: number) => PAD.l + (n === 1 ? 0 : (i / (n - 1)) * innerW);
  const yBar = (v: number) => PAD.t + innerH - (v / maxBar) * innerH;
  const yLine = (v: number) => PAD.t + innerH - (v / maxCum) * innerH;
  const barW = Math.max(8, innerW / n - 4);

  const linePath = "M " + cumulative.map((p, i) => `${x(i)},${yLine(p.value)}`).join(" L ");
  const lastX = x(cumulative.length - 1);
  const firstX = x(0);
  const areaPath = `${linePath} L ${lastX},${PAD.t + innerH} L ${firstX},${PAD.t + innerH} Z`;

  const tickStep = Math.max(1, Math.ceil(n / 6));
  const ticks: number[] = [];
  for (let i = 0; i < n; i += tickStep) ticks.push(i);

  return (
    <ChartCard title={title} sub={sub} wide>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "100%" }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a8fbf" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#1a8fbf" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line
            key={i}
            x1={PAD.l}
            x2={W - PAD.r}
            y1={PAD.t + innerH * p}
            y2={PAD.t + innerH * p}
            stroke="#eef2f7"
            strokeWidth="1"
            strokeDasharray={i === 4 ? "" : "2,3"}
          />
        ))}

        {perPeriod.map((p, i) => {
          const cx = x(i);
          return (
            <rect
              key={i}
              x={cx - barW / 2}
              y={yBar(p.value)}
              width={barW}
              height={PAD.t + innerH - yBar(p.value)}
              fill="#a8d8ea"
              rx="2"
            />
          );
        })}

        <path d={areaPath} fill="url(#areaGrad)" />
        <path d={linePath} fill="none" stroke="#1a8fbf" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {cumulative.map((p, i) => (
          <circle key={i} cx={x(i)} cy={yLine(p.value)} r="3" fill="white" stroke="#1a8fbf" strokeWidth="2" />
        ))}

        {ticks.map((i) => (
          <text key={i} x={x(i)} y={H - 8} fontSize="10" fill="#94a3b8" textAnchor="middle">{perPeriod[i]?.label}</text>
        ))}
        <text x={PAD.l - 6} y={PAD.t + 4} fontSize="10" fill="#94a3b8" textAnchor="end">{maxCum}</text>
        <text x={PAD.l - 6} y={PAD.t + innerH + 2} fontSize="10" fill="#94a3b8" textAnchor="end">0</text>

        <g transform={`translate(${PAD.l}, ${PAD.t - 4})`}>
          <rect x="0" y="-8" width="10" height="6" fill="#a8d8ea" rx="1" />
          <text x="14" y="-2" fontSize="10" fill="#475569">{periodLabel}</text>
          <line x1="74" x2="92" y1="-5" y2="-5" stroke="#1a8fbf" strokeWidth="2" />
          <text x="96" y="-2" fontSize="10" fill="#475569">Cumulative</text>
        </g>
      </svg>
    </ChartCard>
  );
}

const cardCss = `
  .chart-card {
    background: white;
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    padding: 18px 20px;
    box-shadow: var(--shadow-xs);
    display: flex; flex-direction: column;
    min-height: 280px;
  }
  .chart-card.wide { grid-column: 1 / -1; }
  .chart-card-h { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
  .chart-card-t { font-weight: 700; font-size: 14px; color: var(--ink-900); }
  .chart-card-s { font-size: 12px; color: var(--ink-500); margin-top: 2px; }
  .chart-card-body { flex: 1; min-height: 0; display: flex; }

  .hist { display: flex; align-items: flex-end; gap: 8px; flex: 1; padding-top: 16px; }
  .hist-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px; }
  .hist-bar {
    width: 100%; max-width: 56px;
    background: linear-gradient(180deg, var(--brand-400), var(--brand-600));
    border-radius: 6px 6px 0 0;
    position: relative;
    min-height: 6px;
    transition: height 400ms;
  }
  .hist-n { position: absolute; top: -18px; left: 50%; transform: translateX(-50%); font-size: 11px; font-weight: 700; color: var(--ink-700); }
  .hist-l { font-size: 12px; color: var(--ink-500); font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
  .empty-state { padding: 60px; text-align: center; color: var(--ink-500); }
`;

export function SummaryCards({ summary }: {
  summary: {
    totalRegistrants: number;
    paidCount: number;
    paidPercent: number;
    totalPaid: number;
    totalOwing: number;
    membersCount: number;
    membersPercent: number;
    genderSplit: Record<string, number>;
  };
}) {
  const female = summary.genderSplit["Female"] ?? summary.genderSplit["F"] ?? 0;
  const male = summary.genderSplit["Male"] ?? summary.genderSplit["M"] ?? 0;
  const fPct = summary.totalRegistrants ? (female / summary.totalRegistrants) * 100 : 0;
  const unpaidEst = Math.max(0, summary.totalRegistrants - summary.paidCount);

  return (
    <div className="summary-cards">
      <div className="sc">
        <div className="sc-eye">Registrants</div>
        <div className="sc-big">{summary.totalRegistrants}</div>
        <div className="sc-meta">
          <span className="dot" style={{ background: "var(--brand-500)" }} /> live total
        </div>
      </div>

      <div className="sc">
        <div className="sc-eye">Payment</div>
        <div className="sc-big">{summary.paidPercent}<span className="pct">%</span></div>
        <div className="sc-meta">
          <span className="chip green">{summary.paidCount} paid</span>
          {unpaidEst > 0 && <span className="chip red">{unpaidEst} unpaid</span>}
        </div>
      </div>

      <div className="sc">
        <div className="sc-eye">Outstanding</div>
        <div className="sc-big" style={{ color: summary.totalOwing > 0 ? "var(--rose-500)" : "var(--ink-900)" }}>
          ${summary.totalOwing.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </div>
        <div className="sc-meta">${summary.totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })} collected</div>
      </div>

      <div className="sc">
        <div className="sc-eye">Members</div>
        <div className="sc-big">{summary.membersPercent}<span className="pct">%</span></div>
        <div className="sc-meta">{summary.membersCount} active</div>
      </div>

      {(female > 0 || male > 0) && (
        <div className="sc">
          <div className="sc-eye">Gender split</div>
          <div className="gender-bar">
            <div className="gb-f" style={{ width: `${fPct}%` }} />
            <div className="gb-m" style={{ width: `${100 - fPct}%` }} />
          </div>
          <div className="sc-meta sc-meta-row">
            <span><span className="dot" style={{ background: "#c93571" }} /> {female} F</span>
            <span><span className="dot" style={{ background: "#1378a5" }} /> {male} M</span>
          </div>
        </div>
      )}

      <style>{`
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 12px;
          margin-bottom: 14px;
        }
        .sc {
          background: white;
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
          padding: 14px 16px;
          box-shadow: var(--shadow-xs);
        }
        .sc-eye { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-500); margin-bottom: 6px; }
        .sc-big { font-size: 28px; font-weight: 800; color: var(--ink-900); letter-spacing: -0.02em; line-height: 1.1; font-variant-numeric: tabular-nums; }
        .sc-big .pct { font-size: 15px; color: var(--ink-400); margin-left: 1px; }
        .sc-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 6px; font-size: 12px; color: var(--ink-500); }
        .sc-meta .chip { height: 20px; padding: 0 7px; font-size: 11px; }
        .sc-meta .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
        .sc-meta-row { justify-content: space-between; font-weight: 600; color: var(--ink-700); }
        .gender-bar { display: flex; height: 10px; border-radius: var(--r-full); overflow: hidden; margin-top: 14px; background: var(--ink-100); }
        .gb-f { background: #c93571; }
        .gb-m { background: #1378a5; }
      `}</style>
    </div>
  );
}
